import { ToggleGroup, ToggleGroupItem } from '@risksmart-app/atomic-ui';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CopyButton } from './CopyButton';
import type { McpClientId } from './mcpClients';
import { MCP_CLIENTS } from './mcpClients';

const Step: FC<{
  number: number;
  title: string;
  active?: boolean;
  isLast?: boolean;
  children?: ReactNode;
}> = ({ number, title, active = false, isLast = false, children }) => (
  <div className={'relative grid grid-cols-[28px_1fr] gap-3'}>
    {!isLast && (
      <div
        className={'absolute bottom-0 left-[13px] top-8 w-px bg-neutral-border'}
      />
    )}
    <div
      className={`z-10 flex size-7 items-center justify-center rounded-full border text-sm font-bold ${
        active
          ? 'border-secondary bg-secondary text-white'
          : 'border-neutral-border bg-neutral-muted text-neutral-foreground'
      }`}
    >
      {number}
    </div>
    <div className={`flex flex-col gap-2.5 ${isLast ? '' : 'pb-5'}`}>
      <span className={'text-base font-semibold text-foreground'}>{title}</span>
      {children}
    </div>
  </div>
);

/**
 * In-app MCP Personal connection docs (AID-351), rendered inside the
 * integration dialog. Option A layout: a client selector (Claude Desktop /
 * ChatGPT) over a shared four-step flow whose "add" step swaps to the
 * selected client's config snippet.
 */
export const McpPersonalSetup: FC = () => {
  const { t } = useTranslation('common', {
    keyPrefix: 'automations.cards.mcp_personal.setup',
  });

  const serverUrl = getEnv('REACT_APP_MCP_SERVER_URL');
  const [selectedId, setSelectedId] = useState<McpClientId>('claude_desktop');

  const copyLabel = t('copy');
  const copiedLabel = t('copied');

  const selectedClient =
    MCP_CLIENTS.find((client) => client.id === selectedId) ?? MCP_CLIENTS[0];
  const tc = (id: McpClientId, key: string): string =>
    t(`clients.${id}.${key}` as never);
  const clientName = tc(selectedClient.id, 'name');

  return (
    <div className={'flex flex-col gap-4'}>
      <div className={'flex flex-col gap-3'}>
        <h3 className={'text-lg font-semibold text-foreground'}>
          {t('section_title')}
        </h3>

        <ToggleGroup
          aria-label={t('client_selector_label')}
          value={[selectedId]}
          onValueChange={(groupValue: unknown[]) => {
            const next = groupValue[groupValue.length - 1] as
              | McpClientId
              | undefined;
            if (next) {
              setSelectedId(next);
            }
          }}
        >
          {MCP_CLIENTS.map((client) => (
            <ToggleGroupItem
              key={client.id}
              value={client.id}
              className={'px-4 py-1.5 text-sm'}
            >
              {tc(client.id, 'name')}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>

        <div className={'flex flex-col pt-1'}>
          <Step number={1} title={t('steps.url.title')}>
            <div
              className={
                'flex items-center gap-2.5 rounded-lg border border-neutral-border bg-neutral-muted px-3 py-2'
              }
            >
              <span
                className={
                  'flex-1 overflow-x-auto whitespace-nowrap font-mono text-sm text-foreground/80'
                }
              >
                {serverUrl}
              </span>
              <CopyButton
                value={serverUrl}
                copyLabel={copyLabel}
                copiedLabel={copiedLabel}
              />
            </div>
          </Step>

          <Step
            number={2}
            active
            title={t('steps.add.title', { client: clientName })}
          >
            <p className={'text-sm text-foreground/60'}>
              {tc(selectedClient.id, 'add_note')}
            </p>
          </Step>

          <Step number={3} title={t('steps.signin.title')}>
            <p className={'text-sm text-foreground/60'}>
              {t('steps.signin.description')}
            </p>
          </Step>

          <Step number={4} isLast title={t('steps.org.title')}>
            <p className={'text-sm text-foreground/60'}>
              {t('steps.org.description')}
            </p>
          </Step>
        </div>
      </div>
    </div>
  );
};
